import { useMemo, useState, type ReactNode } from 'react';
import { PageCrumbsCtx } from './page-crumbs-context';
import type { Crumb } from './use-breadcrumbs';

/**
 * Lets a detail page contribute its own breadcrumb trail.
 *
 * `useBreadcrumbs` derives the trail from the nav config, which is right for
 * every list page but bottoms out at a generic "Details" on a detail route —
 * so `/patients/:id` read "Patients › Details" whether you were looking at
 * Dawson Schuster or anyone else, and the parent link dropped whatever
 * calendar day you came from.
 *
 * Pages that know better publish their own tail through this context. That is
 * what let the in-page breadcrumb on the appointment header and the back
 * arrow on the patient header both be deleted: there is now ONE trail, in the
 * top bar, and it can say what the page actually is.
 *
 * The value is null on every page that does not opt in, so the derived trail
 * stays the default.
 */
export function PageCrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[] | null>(null);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return (
    <PageCrumbsCtx.Provider value={value}>{children}</PageCrumbsCtx.Provider>
  );
}
